import type { YuzuBlock, YuzuTemplatePage } from '@giftie/yuzu-integration'

/** Finds a block recursively without flattening or translating the Yuzu tree. */
export function findYuzuBlock(blocks: YuzuBlock[], blockId: string): YuzuBlock | null {
  for (const block of blocks) {
    if (block.id === blockId) return block
    const childBlocks = block.children.filter((child): child is YuzuBlock => typeof child === 'object')
    const found = findYuzuBlock(childBlocks, blockId)
    if (found) return found
  }

  return null
}

/** Immutably updates one block in the raw Yuzu page tree. */
export function updateYuzuBlock(
  pages: YuzuTemplatePage[],
  blockId: string,
  update: (block: YuzuBlock) => YuzuBlock,
): YuzuTemplatePage[] {
  return pages.map((page) => ({
    ...page,
    blocks: updateBlocks(page.blocks, blockId, update),
  }))
}

/** Immutably removes one shopper-created block from the raw Yuzu tree. */
export function removeYuzuBlock(
  pages: YuzuTemplatePage[],
  blockId: string,
): YuzuTemplatePage[] {
  return pages.map((page) => ({
    ...page,
    blocks: removeBlockFromChildren(page.blocks, blockId),
  }))
}

/** Applies an immutable update recursively while preserving primitive children. */
function updateBlocks(
  blocks: YuzuBlock[],
  blockId: string,
  update: (block: YuzuBlock) => YuzuBlock,
): YuzuBlock[] {
  return blocks.map((block) => {
    if (block.id === blockId) return update(block)

    return {
      ...block,
      children: block.children.map((child) =>
        typeof child === 'object'
          ? updateBlocks([child], blockId, update)[0] as YuzuBlock
          : child,
      ),
    }
  })
}

/** Removes a block recursively while preserving primitive children. */
function removeBlockFromChildren(blocks: YuzuBlock[], blockId: string): YuzuBlock[] {
  return blocks
    .filter((block) => block.id !== blockId)
    .map((block) => ({
      ...block,
      children: block.children.map((child) => (
        typeof child === 'object'
          ? removeBlockFromChildren([child], blockId)[0]
          : child
      )).filter((child): child is YuzuBlock | string | number => child !== undefined),
    }))
}
